import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'classnames';
import { Popup } from '@progress/kendo-react-popup';
import { translate } from '@utils/i18n';
import { technicianAvatar, technicianTitle } from '@utils/functions/technician.functions';
import { subcontractorIcon, subcontractorTitle } from '@utils/functions/subcontractor.functions';
import { AssigneeType } from '@enums/assignee-type.enum';
import { InlineEdit } from '@components/v2/InlineEdit';
import Chip from '@components/Chip/chip';
import { Button } from '@ui';
import { AssigneeMultiSelectContext, AssigneeMultiSelectProvider } from './AssigneeMultiSelectContext';
import { Loader, LoaderSizes } from '../../Loader/Loader';
import TechSubSelector from './nested/tech-sub-selector';
import TechnicianMultiSelect from '../TechnicianMultiSelect';
import SubcontractorMultiSelect from '../SubcontractorMultiSelect';
import '../GenericMultiSelect.scss';
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function AssigneeMultiSelect({ style = 'default', onlyTechnicians = false, hasSelectAll = false, single = false, fullWidth = false, readOnly = false, includeInactive, onIncludeInactiveChange }) {
    const namespace = 'components.multi_selects.assignee_multi_select';
    const { multiSelectReady, tenant, selectedTechnicians, selectedSubcontractors, technicianList, subcontractorList, activeMultiSelector, setActiveMultiSelector, setSelectedTechnicians, setSelectedSubcontractors } = useContext(AssigneeMultiSelectContext);
    const rootAnchor = useRef(null);
    const popup = useRef(null);
    const subPopup = useRef(null);
    const [open, setOpen] = useState(false);
    const [isSubanchorReady, setIsSubanchorReady] = useState(false);
    const defaultContentPrefix = useMemo(() => (tenant === null || tenant === void 0 ? void 0 : tenant.flags.subcontractorsEnabled)
        ? translate('assigned_to', { namespace })
        : translate('technician', { namespace }), [tenant === null || tenant === void 0 ? void 0 : tenant.flags.subcontractorsEnabled]);
    const showTechSubSelector = useMemo(() => (tenant === null || tenant === void 0 ? void 0 : tenant.flags.subcontractorsEnabled) && !onlyTechnicians, [tenant === null || tenant === void 0 ? void 0 : tenant.flags.subcontractorsEnabled, onlyTechnicians]);
    useEffect(() => {
        if (open && popup.current && activeMultiSelector !== null) {
            setIsSubanchorReady(true);
        }
        else {
            setIsSubanchorReady(false);
        }
    }, [open, popup.current, activeMultiSelector]);
    const handleBodyClick = useCallback((e) => {
        var _a, _b, _c;
        if (!document.body.contains(e.target))
            return;
        if (popup.current && ((_a = popup.current._popup) === null || _a === void 0 ? void 0 : _a.contains(e.target)))
            return;
        if (rootAnchor.current && ((_b = rootAnchor.current) === null || _b === void 0 ? void 0 : _b.contains(e.target)))
            return;
        if (subPopup.current && ((_c = subPopup.current._popup) === null || _c === void 0 ? void 0 : _c.contains(e.target)))
            return;
        setActiveMultiSelector(null);
        setOpen(false);
    }, []);
    useEffect(() => {
        document.body.addEventListener('click', handleBodyClick);
        return () => {
            document.body.removeEventListener('click', handleBodyClick);
        };
    }, []);
    const collectionPreview = useMemo(() => {
        const newSelectedSubs = (tenant === null || tenant === void 0 ? void 0 : tenant.flags.subcontractorsEnabled) ? selectedSubcontractors : [];
        const totalSelections = selectedTechnicians.length + ((tenant === null || tenant === void 0 ? void 0 : tenant.flags.subcontractorsEnabled) ? selectedSubcontractors.length : 0);
        const techPreviews = selectedTechnicians.slice(0, 5).map((tech) => {
            var _a;
            return (React.createElement(React.Fragment, { key: `multiselect-preview-tech-${(_a = tech === null || tech === void 0 ? void 0 : tech.id) !== null && _a !== void 0 ? _a : 'unassigned'}` },
                React.createElement(Chip, { icon: technicianAvatar(tech, 24), label: technicianTitle(tech), showLabel: single })));
        });
        const subPreviews = newSelectedSubs.slice(0, 5 - techPreviews.length).map((sub) => {
            var _a;
            return (React.createElement(React.Fragment, { key: `multiselect-preview-sub-${(_a = sub === null || sub === void 0 ? void 0 : sub.id) !== null && _a !== void 0 ? _a : 'unassigned'}` },
                React.createElement(Chip, { icon: subcontractorIcon(sub, 24), label: subcontractorTitle(sub), showLabel: single })));
        });
        const overflow = totalSelections > 5 ? (React.createElement("span", { key: "assignee-multiselect-overflow", className: "control__count" },
            "+",
            totalSelections - 5)) : null;
        return [...techPreviews, ...subPreviews, overflow];
    }, [selectedTechnicians, selectedSubcontractors, tenant, single]);
    const content = useMemo(() => {
        if (!multiSelectReady)
            return React.createElement(Loader, { size: LoaderSizes.small });
        return (React.createElement("span", { key: "multiselect-preview-content-icons", className: "control__collection" }, collectionPreview));
    }, [multiSelectReady, selectedTechnicians, selectedSubcontractors, tenant]);
    const contentPrefix = useMemo(() => {
        if (style === 'inline-edit' || style === 'modal') {
            return selectedTechnicians.length === 0 && !single ? translate('unassigned', { namespace }) : '';
        }
        return defaultContentPrefix;
    }, [style, defaultContentPrefix, selectedTechnicians, single]);
    const activeMultiSelectorComponent = useMemo(() => {
        if (activeMultiSelector === AssigneeType.TECHNICIAN)
            return (React.createElement(TechnicianMultiSelect, { hasSelectAll: hasSelectAll, selectedTechnicians: selectedTechnicians, technicianList: technicianList, onChange: setSelectedTechnicians, single: single, includeInactive: includeInactive, onIncludeInactiveChange: onIncludeInactiveChange }));
        if (activeMultiSelector === AssigneeType.SUBCONTRACTOR)
            return (React.createElement(SubcontractorMultiSelect, { selectedSubcontractors: selectedSubcontractors, subcontractorList: subcontractorList, onChange: setSelectedSubcontractors, single: single }));
        return null;
    }, [activeMultiSelector, selectedTechnicians, technicianList, selectedSubcontractors, subcontractorList, single, includeInactive, onIncludeInactiveChange]);
    const onReset = useCallback(() => {
        setSelectedTechnicians([]);
        setSelectedSubcontractors([]);
        setOpen(false);
    }, []);
    // When single mode is enabled and a selection is made, close all popups
    useEffect(() => {
        if (single && (selectedTechnicians.length > 0 || selectedSubcontractors.length > 0)) {
            setOpen(false);
            setActiveMultiSelector(null);
        }
    }, [single, selectedTechnicians, selectedSubcontractors]);
    const showMultiSelectorInParent = single && activeMultiSelector !== null && showTechSubSelector;
    const popupContent = useMemo(() => {
        if (showMultiSelectorInParent) {
            return activeMultiSelectorComponent;
        }
        if (showTechSubSelector) {
            return (React.createElement(React.Fragment, null,
                React.createElement(TechSubSelector, { onReset: onReset, single: single }),
                isSubanchorReady && (React.createElement(Popup, { ref: subPopup, anchor: document.getElementById('assignee-parent-popup'), show: activeMultiSelector !== null, anchorAlign: { horizontal: 'right', vertical: 'top' }, popupAlign: { horizontal: 'left', vertical: 'top' } }, activeMultiSelectorComponent))));
        }
        return (React.createElement(TechnicianMultiSelect, { hasSelectAll: hasSelectAll, selectedTechnicians: selectedTechnicians, technicianList: technicianList, onChange: setSelectedTechnicians, single: single, includeInactive: includeInactive, onIncludeInactiveChange: onIncludeInactiveChange }));
    }, [
        showMultiSelectorInParent,
        showTechSubSelector,
        activeMultiSelectorComponent,
        isSubanchorReady,
        activeMultiSelector,
        onReset,
        single,
        hasSelectAll,
        selectedTechnicians,
        technicianList,
        setSelectedTechnicians,
        includeInactive,
        onIncludeInactiveChange
    ]);
    return (React.createElement(React.Fragment, null,
        style === 'filter-panel' && (React.createElement(React.Fragment, null,
            React.createElement("label", { className: "qmb-label", htmlFor: "assigneeButton" }, (tenant === null || tenant === void 0 ? void 0 : tenant.flags.subcontractorsEnabled) ? 'Assigned To' : 'Technician'),
            React.createElement("button", { name: "assigneeButton", className: "filter__value", type: "button", ref: rootAnchor, onClick: () => {
                    setOpen(!open);
                    setActiveMultiSelector(null);
                } }, content))),
        style === 'inline-edit' && (React.createElement(InlineEdit, { id: "technicians-popup-button", as: "div", refComp: rootAnchor, variant: "dropdown", 
            /* eslint-disable-next-line @typescript-eslint/naming-convention */
            className: clsx({ '--x-full': fullWidth }), disableEdit: readOnly, onClick: () => {
                setOpen(!open);
                setActiveMultiSelector(null);
            } },
            contentPrefix,
            " ",
            content)),
        (style === 'default' || style === 'modal') && (React.createElement(Button, { isMenu: true, ref: rootAnchor, onClick: () => {
                setOpen(!open);
                setActiveMultiSelector(null);
            } },
            contentPrefix,
            " ",
            content)),
        React.createElement(Popup, { anchor: rootAnchor.current, show: open, ref: popup, id: "assignee-parent-popup" }, popupContent)));
}
function AssigneeMultiSelectWrapper(params) {
    return (React.createElement(AssigneeMultiSelectProvider, { selectedTechnicianIds: params.selectedTechnicianIds, selectedSubcontractorIds: params.selectedSubcontractorIds, teamIds: params.teamIds, onTechChange: params.onTechChange, onSubcontractorChange: params.onSubChange, single: params.single, skipUnassigned: params.skipUnassigned, forInspections: params.forInspections, buildingId: params.buildingId, extraTechnicians: params.extraTechnicians },
        React.createElement(AssigneeMultiSelect, { style: params.style, onlyTechnicians: params.onlyTechnicians, hasSelectAll: params.hasSelectAll, single: params.single, fullWidth: params.fullWidth, readOnly: params.readOnly, includeInactive: params.includeInactive, onIncludeInactiveChange: params.onIncludeInactiveChange })));
}
export default AssigneeMultiSelectWrapper;
